"use client";
import { Button } from "@cmp-src/primitives/button";
import { VirtualizedSearchableList } from "@/ui/lists/SearchableList/VirtualizedSearchableList";
import { UserListItem } from "@/features/teams/components/TeamSettingsPanel/UserListItem";
import { api } from "@/lib/api/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import { IUserFull } from "@gettactic/api";
import { useUserList } from "@gettactic/hooks";
import { Switch } from "@cmp-src/primitives/Switch";
import { useEffect, useMemo, useState } from "react";

const globalUserFetchInProgress: { [id: string]: boolean } = {};

function useUserListAndRequired(userIds: string[], query: string) {
  const userList = useUserList(query);
  const [missing, setMissing] = useState<{ [id: string]: IUserFull }>({});
  useEffect(() => {
    if (!userList.isSuccess) {
      return;
    }
    const elements = userList.data?.elements ?? [];
    const lookup = userIds.map(async (id) => {
      if (
        elements.find((x) => x.id === id) ||
        missing[id] ||
        globalUserFetchInProgress[id]
      ) {
        return Promise.resolve();
      }
      // we start setting the global dict on true, so we avoid multiple fetch of the same res
      globalUserFetchInProgress[id] = true;
      const user = await api.client.organizations.user(id, true);
      delete globalUserFetchInProgress[id];
      if (user.user) {
        setMissing((x) => ({ ...x, [id]: user.user as IUserFull }));
      }
    });
    Promise.all(lookup).then();
  }, [userList, userIds, userList.isLoading]);

  const list = useMemo(() => {
    const fetched = userList.data?.elements ?? [];
    const notFound = userIds.filter((id) => !fetched.find((x) => x.id === id));
    const foundInMissing = notFound
      .map((x) => missing[x])
      .filter((user) => !!user);
    return fetched.concat(foundInMissing);
  }, [userList, missing, userIds, userList.isLoading]);

  return { list };
}

type Props = {
  ids: string[];
  add: (id: string) => void;
  remove: (id: string) => void;
  isAllUsers?: boolean;
};
export function UsersPermissionList({ ids, add, remove, isAllUsers }: Props) {
  const [currentOver, setCurrentOver] = useState("");
  const [query, setQuery] = useState("");
  const [displayOnlyAssigned, setDisplayOnlyAssigned] = useState(false);
  const { list: listFormIds } = useUserListAndRequired(
    ids.filter((id) => id !== "*"),
    query
  );
  const list: Pick<IUserFull, "id" | "name">[] = isAllUsers
    ? [
        {
          id: "*",
          name: "All users"
        }
      ]
    : listFormIds;

  return (
    <VirtualizedSearchableList
      teamSearchQuery={query}
      setSearchQuery={setQuery}
      scrollAreaClassName={isAllUsers ? "h-auto" : undefined}
      className="pb-0"
      maxRenderedItems={5}
      actionElement={
        <div className="flex items-center gap-3">
          <span className="text-base font-medium text-neutral-900">All</span>
          <Switch
            checked={displayOnlyAssigned}
            onCheckedChange={() => setDisplayOnlyAssigned((x) => !x)}
          />
          <span className="text-base font-medium text-neutral-900">
            Can Reserve
          </span>
        </div>
      }
    >
      {list
        ? list.map((user) => {
            const alreadyIn = ids.indexOf(user.id) > -1;
            if (
              (displayOnlyAssigned && !alreadyIn) ||
              (query &&
                user.name.toLowerCase().indexOf(query.toLowerCase()) === -1)
            ) {
              return null;
            }
            return (
              <UserListItem
                key={user.id}
                user={user}
                rightElement={
                  <Button
                    variant={alreadyIn ? "default" : "outline"}
                    size="sm"
                    onMouseOver={() => setCurrentOver(user.id)}
                    onMouseLeave={() => setCurrentOver("")}
                    onClick={() => (alreadyIn ? remove(user.id) : add(user.id))}
                  >
                    {alreadyIn
                      ? currentOver === user.id
                        ? "Remove"
                        : "Can Reserve"
                      : "Add"}
                  </Button>
                }
              />
            );
          })
        : null}
    </VirtualizedSearchableList>
  );
}
